import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import SearchResultEntity from "../../../../domain/entities/SearchResultEntity";
import UserEntity from "../../../../domain/entities/UserEntity";
import di from "../../../../DependencyInjection";
import SearchUsersUseCase, { SearchUsersUseCaseName } from "../../../../domain/use_cases/user/SearchUsersUseCase";
import DeleteUserUseCase, { DeleteUserUseCaseName } from "../../../../domain/use_cases/user/DeleteUserUseCase";
import CreateUserComponent from "./form/create_user.component";

const useUserListViewModel = () => {
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("search");
    const roleQuery = searchParams.get("role");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalDelete, openModalCustom, closeModalCustom, closeModalDelete } = useContext(ModalsContext);
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm();
    const [result, setResult] = useState<SearchResultEntity<UserEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _searchUsers = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);
        const response = await di.get<SearchUsersUseCase>(SearchUsersUseCaseName).call({
            query: data[KeyWordLocalization.UserListPageQuery],
            page: data[KeyWordLocalization.UserListCurrentPage],
            role: data[KeyWordLocalization.UserListPageRoleQuery],
            itemsPerPage
        });
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data[KeyWordLocalization.UserListPageQuery];
        const page = data[KeyWordLocalization.UserListCurrentPage];
        const role = data[KeyWordLocalization.UserListPageRoleQuery];
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if( role && role.length > 0) query += `role=${role}&`;
        if (page) query += `page=${page}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.user_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = () => {
        setValue(KeyWordLocalization.UserListPageQuery, searchQuery);
        setValue(KeyWordLocalization.UserListCurrentPage, pageQuery);
        setValue(KeyWordLocalization.UserListPageRoleQuery, roleQuery);
        _searchUsers();
    };

    const _handleOnChangePage = (page: number) => {
        setValue(KeyWordLocalization.UserListCurrentPage, page);
        const data = getValues();
        _handleSearch(data);
    };


    const _handleOnClickDeleteAgent = async (agent: UserEntity) => {
        const deleteAgent = async () => {
            const response = await di.get<DeleteUserUseCase>(DeleteUserUseCaseName).call(agent.id);
            if (isLeft(response)) return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
            closeModalDelete();
            addToast(i18n("Usuario eliminado con éxito"), 'success', undefined);
            _searchUsers();
        };
        openModalDelete(
            i18n("Eliminar Usuario"),
            i18n("¿Estás seguro de que deseas eliminar este usuario? esto borrará sus turnos, infomación y todo lo relacionado a él."),
            deleteAgent
        );
    };

    const _handleOnClickUpdateAgent = (agent: UserEntity) => {
        const _onSuccess = () => {
            closeModalCustom();
            _searchUsers();
        };
        openModalCustom(
            "lg",
            i18n("Actualizar Usuario"),
            <CreateUserComponent user={agent} onUpdated={_onSuccess} />,
            { hideOnTap: false }
        );
    };

    const _handleOnCreateSingleAgent = () => {
        const _onSuccess = () => {
            closeModalCustom();
            _searchUsers();
        };
        openModalCustom(
            "lg",
            i18n(KeyWordLocalization.UserListPageAddUser),
            <CreateUserComponent onCreated={_onSuccess} />
        );
    };
     
    useEffect(() => {
        _handleLoad();
    }, [pageQuery, searchQuery, roleQuery]);

    return {
        formInfo: { register, handleSubmit, getValues, setValue, watch, errors },
        result,
        loadingDownload,
        i18n,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleOnChangePage,
        _handleOnClickDeleteAgent,
        _handleOnClickUpdateAgent,
        _handleOnCreateSingleAgent,
    };
};

export default useUserListViewModel;